import { useEffect, useState } from 'react';

import { clientLog } from 'utils/logger';

const log = clientLog.extend('gpt-adunit');
const GPT_SRC = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';

let loadingPromise: Promise<void> | null = null;

/** GPT public API: true immediately after enableServices(); do not rely on local flags. */
function isPubAdsServiceReady(): boolean {
	return Boolean((window.googletag as { pubadsReady?: boolean } | undefined)?.pubadsReady);
}

export function useGpt(options?: { enabled?: boolean }) {
	const enabled = options?.enabled ?? true;
	const [ready, setReady] = useState(false);

	useEffect(() => {
		if (!enabled) return;

		if (window.googletag?.apiReady) {
			log('useGpt: apiReady from page, pubadsReady', isPubAdsServiceReady());
			// apiReady = script loaded; pubadsReady = enableServices() already ran (e.g. Tracker).
			window.googletag.cmd.push(() => {
				if (!isPubAdsServiceReady()) {
					window.googletag.pubads().enableSingleRequest();
					window.googletag.enableServices();
					log('useGpt: enableSingleRequest + enableServices (pubads was not ready)');
				} else {
					log('useGpt: skip enable — pubadsReady already true');
				}
			});
			setReady(true);
			return;
		}

		if (loadingPromise) {
			log('useGpt: reusing loadingPromise');
			loadingPromise.then(() => {
				setReady(true);
			});
			return;
		}

		log('useGpt: loading gpt.js script');
		loadingPromise = new Promise<void>(resolve => {
			window.googletag = window.googletag || { cmd: [] };

			const script = document.createElement('script');
			script.src = GPT_SRC;
			script.async = true;

			script.onload = () => {
				window.googletag.cmd.push(() => {
					if (!isPubAdsServiceReady()) {
						window.googletag.pubads().enableSingleRequest();
						window.googletag.enableServices();
						log('useGpt: enableSingleRequest + enableServices (script onload)');
					} else {
						log('useGpt: skip enable on load — pubadsReady already true');
					}
					resolve();
				});
			};

			document.head.appendChild(script);
		});

		loadingPromise.then(() => {
			setReady(true);
		});
	}, [enabled]);

	return ready;
}
